import { Component, ChangeDetectionStrategy, Input, HostListener, EventEmitter, Output, TemplateRef, input, computed, signal, OnInit, OnChanges, SimpleChanges, DestroyRef, inject } from '@angular/core';
import { IrisFilesService } from '@iris/common/modules/dms/services/files.service';
import { filter, tap } from 'rxjs';
import { IrisDmsFilesSandbox } from '../../../sandbox/dms-files.sandbox';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { IrisDmsModalsSandbox } from '../../../sandbox/dms-modals.sandbox';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IrisCommonDirectivesModule } from '@iris/common/modules/directives-common/common-directives.module';
import { IrisTimeModule } from '@iris/common/modules/time';
import { IrisDMSFileI } from '../../../models/IrisDMSFile';
import { StrakitCheckboxComponent } from '@ngx-strakit/cdk/controls';
import { UnleashService } from '@karelics/angular-unleash-proxy-client';
import { IrisFeatureFlags } from '@iris/common/modules/feature-flags/IrisFeatureFlags';

const SMART_HIDE_THUMBNAIL_EXTENSIONS = ['svg'];
const SMART_HIDE_THUMBNAIL_SIZE = 100000;

@Component({
  selector: 'iris-tile-view-file-card',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatTooltipModule,
    IrisCommonDirectivesModule,
    IrisTimeModule,

    StrakitCheckboxComponent,
  ],
  templateUrl: './tile-view-file-card.component.html',
  styleUrls: ['./tile-view-file-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisTileViewFileCardComponent implements OnInit, OnChanges {
  private readonly filesService = inject(IrisFilesService);
  private readonly dmsFilesSandbox = inject(IrisDmsFilesSandbox);
  private readonly dmsModalsSandbox = inject(IrisDmsModalsSandbox);
  private readonly unleashService = inject(UnleashService);
  private readonly destroyRef = inject(DestroyRef);

  readonly file = input.required<IrisDMSFileI>();

  readonly fileId = computed(() => this.file()?.id);
  readonly fileName = computed(() => this.file()?.originalName ?? this.file()?.name);
  readonly updatedOn = computed(() => this.file()?.updatedOn);
  readonly fileLocked = computed(() => this.file()?.isLocked);

  readonly thumbLink = computed(() => {
    return this.file()?.thumbId ? this.filesService.getFilePreviewUrl(this.file()?.thumbId) : '';
  });

  readonly isImgError = signal(false);

  private readonly smartHideThumbnails = this.unleashService.isEnabled(IrisFeatureFlags.SMART_HIDE_THUMBNAILS);
  private readonly isThumbnailVisible = computed(() => !this.smartHideThumbnails || !SMART_HIDE_THUMBNAIL_EXTENSIONS.includes(this.file()?.extension?.toLowerCase()) || this.file()?.size < SMART_HIDE_THUMBNAIL_SIZE);

  readonly fileTypeIfThereIsNoThumbnail = computed(() => this.filesService.getIconClass(this.file()));
  readonly isThumbnailAvailable = computed(() => !!this.thumbLink() && !this.isImgError() && this.isThumbnailVisible());
  readonly isOfficeEditableDocument = computed(() => this.dmsFilesSandbox.isFileEditable(this.file()));

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    const isCheckboxClick = (event.target as Element).classList.contains('mdc-checkbox__native-control');
    if (isCheckboxClick) {
      event.stopImmediatePropagation();
    } else {
      this.selectionFormControl.setValue(!this.selected);
    }
  }

  @Input() selected = false;
  @Input() lite = false;
  @Input() actionsTemplate: TemplateRef<unknown>;

  @Output() dragging = new EventEmitter<boolean>();
  @Output() selectionChanged = new EventEmitter<boolean>();

  private readonly selectedEntitiesIds = toSignal(this.dmsFilesSandbox.selectedEntitiesIds$);

  readonly dragData = computed(() => {
    const selectedEntitiesIds = this.selectedEntitiesIds();
    const entitiesIds = selectedEntitiesIds.length ? selectedEntitiesIds : [this.fileId()];
    return { entitiesIds };
  });

  readonly selectionFormControl = new FormControl(false);

  ngOnInit(): void {
    this.handleSelection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selected && changes.selected.currentValue !== this.selectionFormControl.value) {
      this.selectionFormControl.setValue(!!changes.selected.currentValue);
    }
  }

  onImgError(): void {
    this.isImgError.set(true);
  }

  openFilePreview(): void {
    if (this.lite) { return; }
    if (this.isOfficeEditableDocument()) {
      this.dmsModalsSandbox.showOfficeEditorModal(this.file());
      return;
    }
    if (!this.filesService.isPreviewReady(this.file())) { return; }
    this.dmsModalsSandbox.showFilePreviewModal(this.file());
  }

  private handleSelection(): void {
    this.selectionFormControl.valueChanges.pipe(
      filter(value => value !== this.selected),
      tap(value => {
        this.selected = value;
        this.selectionChanged.emit(this.selected);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }
}
